import { useState, type ReactNode } from 'react'
import { COLORS } from '../../lib/theme'
import { Icon } from '../Icon'
import { ThemeToggle } from '../ThemeToggle'
import { DEFAULT_MEGA_MENU_COLUMNS } from '../MegaMenu'
import type { NavItem, NavSection } from '../Sidebar'
import { MenuCard, MenuFooter, type MenuColumn } from './menuCard'
import {
  MENU_CARD,
  MENU_DIVIDER,
  PANEL_TOP,
  PANEL_TOP_MOBILE,
  PILL,
  Z,
  isInAppHref,
  useCloseOnEscape,
} from './topbarChrome'

/**
 * The combined "index" menu: one panel holding both the account rail and the
 * marketing tree, as collapsible sections.
 *
 * It exists because the pill carries *two* left-hand triggers wherever the rail
 * is off-canvas — the rail's own handle and the marketing hamburger — and on a
 * phone that is two menus competing for the same corner. This panel is the
 * design's answer: the handle goes, the hamburger opens everything, and the
 * rail becomes the first section of the card the marketing menu already used.
 *
 * The card, its offset and the footer are `MegaMenu`'s — same object, different
 * contents — so the two menus cannot drift apart. What is local to this file is
 * the accordion and the rail rows, which follow the rail's treatment rather
 * than the marketing columns': 40px rows, an icon, a badge and an active stripe.
 */

export interface IndexMenuLabels {
  dialog?: string
  downloadTitle?: string
  /** Heading on the rail's section. */
  railSection?: string
  signOut?: string
}

export interface IndexMenuProps {
  onClose: () => void
  /** The account rail, rendered as the first — and only open — section. */
  navSections?: NavSection[]
  currentPath: string
  /** The marketing tree: one collapsed section per column. */
  columns?: MenuColumn[]
  labels?: IndexMenuLabels
  mobile?: boolean
  /** See MenuColumns — routes in-app links instead of reloading. */
  onNavigateTo?: (href: string) => void
  onSignOut?: () => void
}

/** Sidebar's own matching, so a row is active here exactly when it is there. */
function isActive(pathname: string, href: string) {
  if (href === '/') return pathname === '/'
  return pathname === href || pathname.startsWith(href + '/')
}

function isItemActive(pathname: string, item: NavItem): boolean {
  if (item.external) return false
  if (item.href && isActive(pathname, item.href)) return true
  return (item.children ?? []).some((child) => isItemActive(pathname, child))
}

const ROW_INSET = 12

function railRowStyle(depth: number, active: boolean) {
  return {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    height: 40,
    padding: `0 ${ROW_INSET}px 0 ${ROW_INSET + depth * 16}px`,
    borderRadius: 6,
    fontSize: 16,
    fontWeight: active ? 700 : 400,
    // The card supplies the ink; both states are the same colour, so the
    // active row is told apart by its tint and stripe rather than by hue.
    color: 'inherit',
    textDecoration: 'none',
    position: 'relative' as const,
    boxSizing: 'border-box' as const,
    background: active ? 'var(--wh-nav-active)' : 'transparent',
  }
}

/**
 * A rail row. 16px rather than the rail's own 14 — this card is a phone's whole
 * navigation, not a column beside the content, and the design sizes it up to
 * match the marketing links it sits above.
 */
function RailRow({
  item,
  pathname,
  depth,
  onNavigateTo,
  onClose,
}: {
  item: NavItem
  pathname: string
  depth: number
  onNavigateTo?: (href: string) => void
  onClose: () => void
}) {
  // The same inert "coming soon" row the Sidebar draws: listed, greyed, no link.
  if (item.disabled) {
    return (
      <div
        aria-disabled="true"
        title={item.disabledTitle}
        style={{ ...railRowStyle(depth, false), opacity: 0.55, cursor: 'default' }}
      >
        <span style={{ display: 'flex' }}>
          <Icon name={item.icon} size={20} />
        </span>
        <span style={{ flex: 1 }}>{item.label}</span>
      </div>
    )
  }

  const active = item.href ? isItemActive(pathname, item) : false
  const href = item.href ?? '#'
  return (
    <a
      href={href}
      aria-current={active ? 'page' : undefined}
      onClick={(e) => {
        // An external item is a real navigation away from this app; leave the
        // browser to it and only dismiss the panel.
        if (onNavigateTo && item.href && !item.external && isInAppHref(item.href)) {
          e.preventDefault()
          onNavigateTo(item.href)
        }
        onClose()
      }}
      style={railRowStyle(depth, active)}
      onMouseEnter={(e) => {
        if (!active) e.currentTarget.style.background = 'var(--wh-nav-hover)'
      }}
      onMouseLeave={(e) => {
        if (!active) e.currentTarget.style.background = 'transparent'
      }}
    >
      {active && (
        <span
          aria-hidden
          style={{ position: 'absolute', left: 0, top: 8, bottom: 8, width: 3, background: COLORS.primary }}
        />
      )}
      {/* Held back on the inactive rows so the label leads and the icons read
          as a column of marks rather than competing with it. */}
      <span style={{ display: 'flex', opacity: active ? 1 : 0.75 }}>
        <Icon name={item.icon} size={20} />
      </span>
      <span style={{ flex: 1 }}>{item.label}</span>
      {item.badge != null && (
        <span
          style={{
            minWidth: 22,
            height: 22,
            padding: '0 6px',
            borderRadius: 999,
            background: 'var(--nl-primary-opacity-12)',
            color: COLORS.primary,
            fontSize: 11,
            fontWeight: 700,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {item.badge}
        </span>
      )}
    </a>
  )
}

/**
 * The 11px uppercase group label the rail uses between its sections.
 *
 * Held-back card ink rather than `COLORS.outline`, which the rail can afford
 * because it sits on `primaryContainer`. On this card's dark-theme sage,
 * `outline` all but disappears; the card's own on-colour cannot, in either
 * theme, and at 65% it still reads as a label and not as a row.
 */
function Overline({ children }: { children: string }) {
  return (
    <div
      style={{
        fontSize: 11,
        fontWeight: 700,
        color: 'inherit',
        opacity: 0.65,
        textTransform: 'uppercase',
        letterSpacing: 0.8,
        padding: `16px ${ROW_INSET}px 8px`,
      }}
    >
      {children}
    </div>
  )
}

/**
 * One collapsible section. The heading carries the card's own title size, so
 * the rail's section and the marketing ones are peers — which is the whole
 * point of the direction: one index, four entries, no hierarchy between "your
 * account" and "the shop".
 */
function Section({
  title,
  open,
  onToggle,
  first,
  children,
}: {
  title: string
  open: boolean
  onToggle: () => void
  /** The first section owns the card's top edge, so it takes no rule. */
  first?: boolean
  children: ReactNode
}) {
  return (
    <div
      style={
        first
          ? undefined
          : { marginTop: 24, paddingTop: 24, borderTop: `1px solid ${MENU_DIVIDER}` }
      }
    >
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        style={{
          width: '100%',
          border: 'none',
          background: 'transparent',
          padding: 0,
          cursor: 'pointer',
          textAlign: 'left',
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          fontFamily: 'inherit',
          fontSize: MENU_CARD.titleSize,
          fontWeight: 700,
          lineHeight: 1.3,
          color: 'inherit',
        }}
      >
        {title}
        <span style={{ flex: 1 }} />
        <span aria-hidden style={{ display: 'flex', opacity: 0.7 }}>
          <Icon name={open ? 'expand_less' : 'expand_more'} size={24} />
        </span>
      </button>
      {open && children}
    </div>
  )
}

export function IndexMenu({
  onClose,
  navSections = [],
  currentPath,
  // The same default tree as MegaMenu, so the two menus show the same links
  // unless the shell substitutes its own.
  columns = DEFAULT_MEGA_MENU_COLUMNS,
  labels,
  mobile = false,
  onNavigateTo,
  onSignOut,
}: IndexMenuProps) {
  const railTitle = labels?.railSection ?? 'Account'
  // Keyed by position, not by heading: the headings are content — a localised
  // tree or a renamed column would collide with the rail's own title.
  //
  // One section at a time, and the rail is the one that starts open: it is
  // where a signed-in user is going, and it is the half that was behind a
  // second, now-deleted control. The marketing tree was one tap away before and
  // stays one tap away.
  const [open, setOpen] = useState<string | null>('rail')
  const toggle = (key: string) => setOpen((cur) => (cur === key ? null : key))
  useCloseOnEscape(true, onClose)

  const top = mobile ? PANEL_TOP_MOBILE : PANEL_TOP

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={labels?.dialog ?? 'Navigation menu'}
      style={{
        position: 'fixed',
        top,
        left: 0,
        right: 0,
        zIndex: Z.panel,
        display: 'flex',
        justifyContent: 'center',
        padding: mobile ? '0 16px' : '0 24px',
        pointerEvents: 'none',
      }}
    >
      <div
        style={{
          pointerEvents: 'auto',
          width: '100%',
          // Capped at the pill rather than at the marketing card's 1123: this
          // is a single stacked index, and stretching it to a desktop card
          // would strand four 24px headings in a very wide, very empty box.
          maxWidth: PILL.maxWidth,
          maxHeight: `calc(100dvh - ${top + 24}px)`,
          overflowY: 'auto',
          overscrollBehavior: 'contain',
        }}
      >
        <MenuCard mobile={mobile}>
          {navSections.length > 0 && (
            <Section first title={railTitle} open={open === 'rail'} onToggle={() => toggle('rail')}>
              {/* Negative inset so the rows' own 12px padding lines their text
                  up with the headings while their tint and stripe still run
                  the full width of the card. */}
              <div style={{ paddingTop: 16, margin: `0 -${ROW_INSET}px` }}>
                {navSections.map((section, i) => (
                  <div key={section.label ?? `section-${i}`}>
                    {/* The first group's own label is dropped: the section
                        heading above has just said it. */}
                    {i > 0 && section.label && <Overline>{section.label}</Overline>}
                    {section.items.map((item) => (
                      <div key={item.id}>
                        <RailRow
                          item={item}
                          pathname={currentPath}
                          depth={0}
                          onNavigateTo={onNavigateTo}
                          onClose={onClose}
                        />
                        {(item.children ?? []).map((child) => (
                          <RailRow
                            key={child.id}
                            item={child}
                            pathname={currentPath}
                            depth={1}
                            onNavigateTo={onNavigateTo}
                            onClose={onClose}
                          />
                        ))}
                      </div>
                    ))}
                  </div>
                ))}

                {/* The rail's footer. Without it the theme switch and the only
                    way to sign out would go down with the drawer this panel
                    replaces. */}
                <div
                  style={{
                    marginTop: 16,
                    paddingTop: 8,
                    borderTop: `1px solid ${MENU_DIVIDER}`,
                  }}
                >
                  <ThemeToggle />
                  <a
                    href="/sign-out"
                    onClick={(e) => {
                      if (onSignOut) {
                        e.preventDefault()
                        onSignOut()
                      }
                      onClose()
                    }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 12,
                      height: 40,
                      padding: `0 ${ROW_INSET}px`,
                      borderRadius: 6,
                      fontSize: 16,
                      color: 'inherit',
                      textDecoration: 'none',
                    }}
                  >
                    <span style={{ display: 'flex', opacity: 0.75 }}>
                      <Icon name="logout" size={20} />
                    </span>
                    <span>{labels?.signOut ?? 'Sign out'}</span>
                  </a>
                </div>
              </div>
            </Section>
          )}

          {columns.map((col, i) => (
            <Section
              key={col.title}
              first={i === 0 && navSections.length === 0}
              title={col.title}
              open={open === `col:${i}`}
              onToggle={() => toggle(`col:${i}`)}
            >
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: MENU_CARD.linkGap,
                  paddingTop: 24,
                  fontSize: MENU_CARD.linkSize,
                  lineHeight: 1.3,
                }}
              >
                {col.items.map((item) => (
                  <a
                    key={item.label}
                    href={item.href}
                    onClick={(e) => {
                      if (onNavigateTo && isInAppHref(item.href)) {
                        e.preventDefault()
                        onNavigateTo(item.href)
                      }
                      onClose()
                    }}
                    style={{ color: 'inherit', textDecoration: 'none' }}
                  >
                    {item.label}
                  </a>
                ))}
              </div>
            </Section>
          ))}

          <MenuFooter title={labels?.downloadTitle} />
        </MenuCard>
      </div>
    </div>
  )
}
